import { useEffect, useState } from 'react';
import WriteReviewModal from './WriteReviewModal';
import { createReview } from '../../services/reviews';
import { publishAppToast } from '../../utils/appToast';
import { formatReviewDate } from '../../utils/courseMappers';

const STAR_PATH =
  'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';

function DemoUserIcon() {
  return (
    <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  );
}

function ReviewAvatar({ name, avatar }) {
  const [failed, setFailed] = useState(false);

  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#D4C4F5] to-[#9369CC]">
      {avatar && !failed ? (
        <img src={avatar} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <DemoUserIcon />
      )}
    </div>
  );
}

function StudentsReviewSection({
  reviews,
  courseId,
  canWriteReview = false,
  onReviewCreated,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  if (!reviews) return null;

  const handleSubmitReview = async ({ rating, comment }) => {
    if (!courseId || submitting) return;

    setSubmitting(true);
    try {
      const response = await createReview({ courseId, rating, comment });
      publishAppToast(response?.message || 'Review created successfully', 'success');
      setModalOpen(false);
      await onReviewCreated?.(response?.data);
    } catch (error) {
      publishAppToast(error?.message || 'Failed to submit review', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[24px] font-semibold text-[#1B1B1B]">Students Review</h2>
        {canWriteReview ? (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="rounded-full bg-[#9369CC] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#7f55b8]"
          >
            Write A Review
          </button>
        ) : null}
      </div>

      <div className="mt-6 flex flex-col gap-8 p-6 sm:flex-row sm:p-8">
        <div className="shrink-0 text-center sm:w-48">
          <p className="text-[32px] font-semibold text-[#1B1B1B]">{reviews.average.toFixed(1)}</p>
          <div className="mt-2 flex justify-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <svg key={star} className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <path d={STAR_PATH} />
              </svg>
            ))}
          </div>
          <p className="mt-2 text-[20px] font-medium text-[#999999]">{reviews.totalRatings}</p>
        </div>

        <div className="flex-1 space-y-2">
          {reviews.distribution.map((row) => (
            <div key={row.stars} className="flex items-center gap-3">
              <span className="w-3 text-[14px] font-medium text-[#9D9D9D]">{row.stars}</span>
              <svg className="h-3.5 w-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <path d={STAR_PATH} />
              </svg>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#E9E9E9]">
                <div
                  className="h-full rounded-full bg-amber-400"
                  style={{ width: `${row.percent}%` }}
                />
              </div>
              <span className="w-10 text-right text-[14px] font-medium text-[#9D9D9D]">{row.percent}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-6">
        {reviews.items.map((review) => (
          <article key={review.id} className="p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <ReviewAvatar name={review.name} avatar={review.avatar} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-[14px] font-semibold text-[#666666]">{review.name}</h3>
                  <span className="text-[12px] text-[#6E7485]">
                    • {formatReviewDate(review.createdAt) || review.timeAgo}
                  </span>
                </div>
                <div className="mt-1 flex gap-0.5">
                  {Array.from({ length: review.rating }, (_, i) => (
                    <svg key={i} className="h-4 w-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                      <path d={STAR_PATH} />
                    </svg>
                  ))}
                </div>
                <p className="mt-3 text-base leading-relaxed text-[#6B7781]">{review.text}</p>
              </div>
            </div>
          </article>
        ))}
      </div>

      <WriteReviewModal
        open={modalOpen}
        onClose={() => {
          if (!submitting) setModalOpen(false);
        }}
        onSubmit={handleSubmitReview}
        submitting={submitting}
      />
    </section>
  );
}

export default StudentsReviewSection;
